<script setup>
/**
 * General workspace settings.
 *
 * Intercom's General page grammar: stacked section cards in the narrow column -
 * workspace details, default language, feature-gated extras, the account's
 * identifiers, and the danger zone last. `Save changes` lives in the page
 * header and stays disabled until a field actually changes; the transcription
 * toggle applies immediately, which is why it sits among the feature rows
 * rather than in the form.
 *
 * Workspace details carries two names: `name` is what the team sees inside the
 * app, and `customer_facing_name` is the brand customers see on outgoing
 * surfaces. The second is optional - left blank, the backend's
 * `Account#brand_name` falls back to the internal name.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import SettingsFieldGrid from 'dashboard/components-next/Settings/SettingsFieldGrid.vue';
import SettingsField from 'dashboard/components-next/Settings/SettingsField.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import AccountId from './components/AccountId.vue';
import AccountDelete from './components/AccountDelete.vue';
import AudioTranscription from './components/AudioTranscription.vue';

const { t, locale: activeLocale } = useI18n({ useScope: 'global' });
const store = useStore();
const { uiSettings } = useUISettings();
const { enabledLanguages } = useConfig();
const { accountId, currentAccount } = useAccount();

const uiFlags = useMapGetter('accounts/getUIFlags');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isOnChatwootCloud = useMapGetter('globalConfig/isOnChatwootCloud');

const name = ref('');
const customerFacingName = ref('');
const locale = ref('en');
const domain = ref('');
const supportEmail = ref('');
// What the account looked like when it was last loaded or saved; the header's
// Save button is driven by the difference between this and the fields above.
const savedValues = ref({});

const v$ = useVuelidate(
  { name: { required }, locale: { required } },
  { name, locale }
);

const languagesSortedByCode = computed(() =>
  [...enabledLanguages].sort((first, second) =>
    first.iso_639_1_code.localeCompare(second.iso_639_1_code)
  )
);

const features = computed(() => currentAccount.value?.features || {});
const showCustomReplyDomain = computed(
  () => !!features.value.inbound_emails && !!features.value.custom_reply_domain
);
const showCustomReplyEmail = computed(
  () => !!features.value.inbound_emails && !!features.value.custom_reply_email
);
const showAudioTranscription = computed(() =>
  isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.CAPTAIN)
);
// The card only exists when at least one feature-gated row does.
const showFeaturesCard = computed(
  () =>
    showCustomReplyDomain.value ||
    showCustomReplyEmail.value ||
    showAudioTranscription.value
);

const currentValues = computed(() => ({
  name: name.value,
  customerFacingName: customerFacingName.value,
  locale: locale.value,
  domain: domain.value,
  supportEmail: supportEmail.value,
}));

const isDirty = computed(
  () =>
    JSON.stringify(currentValues.value) !== JSON.stringify(savedValues.value)
);

// A user-level language choice wins over the account's; the account locale only
// takes effect for people who have not picked one.
const applyLocale = value => {
  const effectiveLocale = uiSettings.value?.locale || value;
  if (effectiveLocale) activeLocale.value = effectiveLocale;
};

watch(
  currentAccount,
  account => {
    if (!account?.id) return;

    name.value = account.name;
    customerFacingName.value = account.customer_facing_name || '';
    locale.value = account.locale;
    domain.value = account.domain;
    supportEmail.value = account.support_email;
    savedValues.value = { ...currentValues.value };
    applyLocale(account.locale);
  },
  { immediate: true }
);

const updateAccount = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    useAlert(t('GENERAL_SETTINGS.FORM.ERROR'));
    return;
  }

  try {
    await store.dispatch('accounts/update', {
      locale: locale.value,
      name: name.value,
      customer_facing_name: customerFacingName.value,
      domain: domain.value,
      support_email: supportEmail.value,
    });
    savedValues.value = { ...currentValues.value };
    applyLocale(locale.value);
    useAlert(t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.UPDATE.ERROR'));
  }
};
</script>

<template>
  <SettingsPageShell
    :title="$t('SIDEBAR.COMPANY_DETAILS')"
    show-save
    :is-dirty="isDirty"
    :is-saving="uiFlags.isUpdating"
    @save="updateAccount"
  >
    <SettingsSection
      :title="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.TITLE')"
      :description="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.NOTE')"
    >
      <div class="p-4">
        <SettingsFieldGrid v-if="!uiFlags.isFetchingItem">
          <SettingsField
            class="sm:col-span-2"
            :label="$t('GENERAL_SETTINGS.FORM.NAME.LABEL')"
            :help-text="$t('GENERAL_SETTINGS.FORM.NAME.HELP_TEXT')"
            :error-message="
              v$.name.$error ? $t('GENERAL_SETTINGS.FORM.NAME.ERROR') : ''
            "
          >
            <Input
              v-model="name"
              :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
              @blur="v$.name.$touch"
            />
          </SettingsField>
          <SettingsField
            class="sm:col-span-2"
            :label="$t('GENERAL_SETTINGS.FORM.CUSTOMER_FACING_NAME.LABEL')"
            :help-text="
              $t('GENERAL_SETTINGS.FORM.CUSTOMER_FACING_NAME.HELP_TEXT')
            "
          >
            <Input
              v-model="customerFacingName"
              :placeholder="
                name || $t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')
              "
            />
          </SettingsField>
        </SettingsFieldGrid>
        <SettingsFieldGrid v-else aria-busy="true">
          <div v-for="field in 2" :key="field" class="flex flex-col gap-1.5">
            <Skeleton variant="text" class="w-24" :announce="field === 1" />
            <Skeleton class="w-full h-10" :announce="false" />
          </div>
        </SettingsFieldGrid>
      </div>
    </SettingsSection>

    <SettingsSection
      :title="$t('GENERAL_SETTINGS.FORM.LANGUAGE_SECTION.TITLE')"
      :description="$t('GENERAL_SETTINGS.FORM.LANGUAGE_SECTION.NOTE')"
    >
      <div class="p-4">
        <SettingsFieldGrid>
          <SettingsField :label="$t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL')">
            <select
              v-model="locale"
              class="block w-full h-10 py-2.5 !mb-0 text-sm border-none rounded-lg outline outline-1 outline-offset-[-1px] outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand bg-n-alpha-black2 text-n-slate-12"
            >
              <option
                v-for="language in languagesSortedByCode"
                :key="language.iso_639_1_code"
                :value="language.iso_639_1_code"
              >
                {{ language.name }}
              </option>
            </select>
          </SettingsField>
        </SettingsFieldGrid>
      </div>
    </SettingsSection>

    <SettingsSection
      v-if="showFeaturesCard"
      :title="$t('GENERAL_SETTINGS.FORM.FEATURES_SECTION.TITLE')"
      :description="$t('GENERAL_SETTINGS.FORM.FEATURES_SECTION.NOTE')"
    >
      <div v-if="showCustomReplyDomain || showCustomReplyEmail" class="p-4">
        <SettingsFieldGrid>
          <SettingsField
            v-if="showCustomReplyDomain"
            :label="$t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL')"
            :help-text="
              $t('GENERAL_SETTINGS.FORM.FEATURES.CUSTOM_EMAIL_DOMAIN_ENABLED')
            "
          >
            <Input
              v-model="domain"
              :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
            />
          </SettingsField>
          <SettingsField
            v-if="showCustomReplyEmail"
            :label="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL')"
          >
            <Input
              v-model="supportEmail"
              :placeholder="
                $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')
              "
            />
          </SettingsField>
        </SettingsFieldGrid>
      </div>
      <AudioTranscription v-if="showAudioTranscription" />
    </SettingsSection>

    <AccountId />
    <AccountDelete v-if="!uiFlags.isFetchingItem && isOnChatwootCloud" />
  </SettingsPageShell>
</template>
